/*
  ecomific-availability-controller.js
  Step 4.3 controller:
  - fetch regional availability via Inventory API
  - normalize to: available | coming_soon | sold_out | unknown
  - apply via Step 4.8 DOM contract
*/

(() => {
    if (window.__ecomificAvailabilityController) {
      window.__ecomificAvailabilityController.init?.();
      return;
    }
    window.__ecomificAvailabilityControllerInitialized = true;

    const LOG_NS = "ecomific";
    const MODULE = "availability-controller";
    const DAY_MS = 24 * 60 * 60 * 1000;

    function isDebugEnabled() {
      if (typeof window === "undefined") return false;
      if (window.ecomificDebug === true || window.__ecomificDebug === true) {
        return true;
      }

      try {
        if (window.localStorage?.getItem?.("ecomific_debug") === "1") {
          return true;
        }
      } catch (err) {}

      try {
        if (window.location?.search) {
          const params = new URLSearchParams(window.location.search);
          if (params.has("ecomificDebug")) {
            return true;
          }
        }
      } catch (err) {}

      return false;
    }

    function fmt(level, msg) {
      return `[${LOG_NS}][${level}] ${MODULE}: ${msg}`;
    }

    function logDebug(msg, ...args) {
      if (!isDebugEnabled()) return;
      console.debug(fmt("debug", msg), ...args);
    }

    function logInfo(msg, ...args) {
      if (!isDebugEnabled()) return;
      console.info(fmt("info", msg), ...args);
    }

    function logWarn(msg, ...args) {
      console.warn(fmt("warn", msg), ...args);
    }

    function logError(msg, ...args) {
      console.error(fmt("error", msg), ...args);
    }
  
    const DEFAULTS = {
      endpointBase: "", // expects `${base}/inventory?...`
      requestTimeoutMs: 4500,
      maxConcurrentRequests: 3,
      sessionCacheTtlMs: 3 * 60 * 1000,
  
      // Step 4.5 (resolved)
      lowStockThreshold: 5,
      lowStockCopy: "Low stock",
      showExactQtyWhenLowStock: false,
  
      // Step 4.6 (recommended default)
      blockAtcWhenUnknown: true,
      unknownCopy: "Availability couldn’t be confirmed right now.",
      loadingCopy: "Checking availability…",
  
      // Step 4.7 (needs a theme setting later; default here is conservative-ish)
      launchPendingMaxDays: 30,
  
      // Copy defaults (can become theme settings later if you want)
      comingSoonCopy: "Coming soon",
      soldOutCopy: "Out of stock",
      onlyXLeftPrefix: "Only ",
      onlyXLeftSuffix: " left",
    };

    const CONTAINER_SELECTOR = "[data-ecomific-product],[data-ecomific-pdp]";
    const PDP_DOM = {
      root: "[data-ecomific-pdp]",
      atcPrimary: "[data-ecomific-pdp-atc]",
      atcSecondary: "[data-ecomific-pdp-atc-secondary]",
      qtyInput: "[data-ecomific-pdp-qty]",
      availability: "[data-ecomific-pdp-availability]",
      lowStock: "[data-ecomific-pdp-lowstock]",
      status: "[data-ecomific-pdp-status]",
    };
    const PDP_STATE = Object.freeze({
      loading: "loading",
      available: "available",
      unavailable: "unavailable",
      unknown: "unknown",
    });
    const PDP_ATC_REASSERT_DELAYS_MS = [0, 90, 220];
    const SESSION_CACHE_PREFIX = "ecomificAvailability:";
    const REGION_LS_KEY = "ecomific_region";
    const REGION_COOKIE = "ecomific_region";

    let cachedRegionFromEvents = null;

    function getRegionFromStorage() {
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          const ls = window.localStorage.getItem(REGION_LS_KEY);
          if (ls) return normalizeRegion(ls);
        }
      } catch (err) {}
      if (typeof document !== "undefined" && document.cookie) {
        const m = document.cookie.match(new RegExp("(?:^|; )" + REGION_COOKIE + "=([^;]*)"));
        const val = m ? decodeURIComponent(m[1]) : null;
        if (val) return normalizeRegion(val);
      }
      return null;
    }

    function getConfig() {
      const cfg = (window.ecomificAvailabilityConfig && typeof window.ecomificAvailabilityConfig === "object")
        ? window.ecomificAvailabilityConfig
        : {};
      const endpointBase = cfg.endpointBase || cfg.inventoryApiBase || DEFAULTS.endpointBase;
      return { ...DEFAULTS, ...cfg, endpointBase };
    }
  
    function withTimeout(promise, ms) {
      return new Promise((resolve, reject) => {
        const t = setTimeout(() => reject(new Error("timeout")), ms);
        promise.then(
          (v) => { clearTimeout(t); resolve(v); },
          (e) => { clearTimeout(t); reject(e); }
        );
      });
    }
  
    function normalizeVariantId(raw) {
      if (!raw) return null;
      // Accept numeric, string numeric, or gid://shopify/ProductVariant/123
      const s = String(raw);
      const gidMatch = s.match(/ProductVariant\/(\d+)/);
      if (gidMatch) return gidMatch[1];
      const numMatch = s.match(/^\d+$/);
      if (numMatch) return s;
      return s; // let API normalize if it can
    }
  
    function parseBool(v) {
      if (v === true || v === false) return v;
      if (v == null) return false;
      const s = String(v).toLowerCase();
      return s === "true" || s === "1" || s === "yes";
    }
  
    function parseDateMs(v) {
      if (!v) return null;
      const t = Date.parse(String(v));
      return Number.isFinite(t) ? t : null;
    }
  
    function normalizeRegion(value) {
      if (typeof value !== "string") return null;
      const normalized = value.trim().toLowerCase();
      return normalized || null;
    }

    function normalizeEndpointBase(value) {
      const raw = String(value || "").trim();
      if (!raw) return "";
      if (!/^https?:\/\//i.test(raw)) return "";
      return raw.replace(/\/+$/, "");
    }

    function buildInventoryEndpoint(base) {
      if (!base) return "";

      // Check for /inventory on the path portion only (exclude any ?query or #hash),
      // while preserving the original suffix.
      const hashIndex = base.indexOf("#");
      const queryIndex = base.indexOf("?");
      const cutIndex =
        hashIndex === -1
          ? queryIndex
          : queryIndex === -1
          ? hashIndex
          : Math.min(hashIndex, queryIndex);

      const pathPart = cutIndex === -1 ? base : base.slice(0, cutIndex);
      const suffix = cutIndex === -1 ? "" : base.slice(cutIndex);

      const normalizedPath = pathPart.replace(/\/+$/, "");
      const finalPath = normalizedPath.endsWith("/inventory")
        ? normalizedPath
        : `${normalizedPath}/inventory`;

      return `${finalPath}${suffix}`;
    }
  
    function getActiveRegionFromDomOrCart() {
      // Region from events (cached on ready/changed), then storage, then cart.
      const fromCache = cachedRegionFromEvents === "east" || cachedRegionFromEvents === "west"
        ? cachedRegionFromEvents
        : null;
      if (fromCache) return Promise.resolve(fromCache);

      const fromStorage = getRegionFromStorage();
      if (fromStorage) return Promise.resolve(fromStorage);

      return fetch("/cart.js", { credentials: "same-origin" })
        .then((r) => r.json())
        .then((cart) => normalizeRegion(cart?.attributes?.region) || null)
        .catch(() => null);
    }

    function getPdpEls(root = document) {
      function isElementVisible(el) {
        if (!el || typeof el.getClientRects !== "function") return false;
        if (el.closest?.("[hidden]")) return false;
        const style = window.getComputedStyle ? window.getComputedStyle(el) : null;
        if (style && (style.display === "none" || style.visibility === "hidden")) {
          return false;
        }
        return el.getClientRects().length > 0;
      }

      function pickPreferredElement(candidates) {
        const list = Array.isArray(candidates) ? candidates.filter(Boolean) : [];
        if (!list.length) return null;
        return list.find((el) => isElementVisible(el)) || list[0] || null;
      }

      function normalizeAtcButtons(candidates) {
        const seen = new Set();
        const buttons = [];
        const list = Array.isArray(candidates) ? candidates : [];

        for (const candidate of list) {
          if (!candidate) continue;
          const button = candidate.matches?.("button, input[type='submit'], input[type='button']")
            ? candidate
            : candidate.querySelector?.("button, input[type='submit'], input[type='button']");
          if (!button || seen.has(button)) continue;
          seen.add(button);
          buttons.push(button);
        }

        return buttons;
      }

      const scope = root && root.nodeType === 1 ? root : document;
      const pdpRoot =
        scope.matches?.(PDP_DOM.root)
          ? scope
          : scope.querySelector?.(PDP_DOM.root);
      const queryScope = pdpRoot || scope;

      const atcCandidates = []
        .concat(Array.from(queryScope.querySelectorAll?.(PDP_DOM.atcPrimary) || []))
        .concat(Array.from(queryScope.querySelectorAll?.(PDP_DOM.atcSecondary) || []))
        .concat(Array.from(queryScope.querySelectorAll?.("[data-ecomific-atc]") || []));
      const atcButtons = normalizeAtcButtons(atcCandidates);

      const qtyCandidates = []
        .concat(Array.from(queryScope.querySelectorAll?.(PDP_DOM.qtyInput) || []))
        .concat(Array.from(queryScope.querySelectorAll?.("[data-ecomific-qty-input]") || []));
      const availabilityCandidates = []
        .concat(Array.from(queryScope.querySelectorAll?.(PDP_DOM.availability) || []))
        .concat(Array.from(queryScope.querySelectorAll?.("[data-ecomific-availability-message]") || []));

      return {
        root: pdpRoot || null,
        atcButtons,
        qtyInput: pickPreferredElement(qtyCandidates),
        availabilityEl: pickPreferredElement(availabilityCandidates),
        lowStockEl: queryScope.querySelector?.(PDP_DOM.lowStock) || null,
        statusEl: queryScope.querySelector?.(PDP_DOM.status) || null,
      };
    }

    function logPdpDomContract(container, els) {
      if (!isDebugEnabled() || !container || !els) return;
      if (container.__ecomificPdpDomLogged) return;
      container.__ecomificPdpDomLogged = true;

      logInfo("pdp dom contract", {
        atcButtons: els.atcButtons.length,
        qtyInput: !!els.qtyInput,
        availabilityEl: !!els.availabilityEl,
        lowStockEl: !!els.lowStockEl,
        statusEl: !!els.statusEl,
      });

      if (els.atcButtons.length < 1) {
        logWarn("pdp dom missing critical element: ATC button");
      }
      if (!els.qtyInput) {
        logWarn("pdp dom missing critical element: qty input");
      }
    }

    function setAtcEnabled(atcButtons, enabled) {
      const buttons = Array.isArray(atcButtons) ? atcButtons : [];
      for (const button of buttons) {
        if (!button) continue;
        button.disabled = !enabled;
        if (button.classList) {
          button.classList.toggle("disabled", !enabled);
        }
        if (enabled) {
          button.removeAttribute("aria-disabled");
        } else {
          button.setAttribute("aria-disabled", "true");
        }
      }
    }

    function readAtcLabel(button) {
      if (!button) return "";
      const textNode = button.querySelector?.("[data-add-to-cart-text]");
      const raw = textNode ? textNode.textContent : button.textContent;
      return String(raw || "").trim();
    }

    function readAtcDefaultLabel(button) {
      if (!button) return "";
      const textNode = button.querySelector?.("[data-add-to-cart-text]");
      if (textNode) {
        const datasetDefault = String(textNode.dataset?.defaultText || "").trim();
        if (datasetDefault) return datasetDefault;
        if (!textNode.dataset.ecomificDefaultText) {
          textNode.dataset.ecomificDefaultText = String(textNode.textContent || "").trim();
        }
        return String(textNode.dataset.ecomificDefaultText || "").trim();
      }

      if (!button.dataset.ecomificDefaultText) {
        button.dataset.ecomificDefaultText = String(button.textContent || "").trim();
      }
      return String(button.dataset.ecomificDefaultText || "").trim();
    }

    function readAtcSoldOutLabel(button) {
      if (!button) return "";
      const textNode = button.querySelector?.("[data-add-to-cart-text]");
      if (textNode) {
        const datasetSoldOut = String(textNode.dataset?.soldOutText || "").trim();
        if (datasetSoldOut) return datasetSoldOut;
      }
      const buttonSoldOut = String(button.dataset?.ecomificSoldOutText || "").trim();
      return buttonSoldOut || "";
    }

    function setAtcLabel(button, label) {
      if (!button || !label) return;
      const textNode = button.querySelector?.("[data-add-to-cart-text]");
      if (textNode) {
        textNode.textContent = label;
      } else {
        button.textContent = label;
      }
    }

    function syncCardAtcLabels(atcSlots, state, cfg) {
      const elements = normalizeElements(atcSlots);
      for (const el of elements) {
        if (!el) continue;
        const button = el.matches?.("button, input[type='submit'], input[type='button']")
          ? el
          : el.querySelector?.("button, input[type='submit'], input[type='button']");
        if (!button) continue;

        const defaultLabel = readAtcDefaultLabel(button) || "Add to Cart";
        const soldOutLabel = String(button.dataset?.ecomificSoldOutText || "").trim() || cfg.soldOutCopy || "Sold Out";
        const comingSoonLabel = String(button.dataset?.ecomificComingSoonText || "").trim() || cfg.comingSoonCopy || "Coming soon";
        const nextLabel =
          state.status === "sold_out" ? soldOutLabel :
          state.status === "coming_soon" ? comingSoonLabel :
          defaultLabel;

        if (readAtcLabel(button) !== nextLabel) {
          setAtcLabel(button, nextLabel);
        }
      }
    }

    function getExpectedPdpAtcLabel(button, enabled, status) {
      const defaultLabel = readAtcDefaultLabel(button);
      if (enabled) return defaultLabel;
      if (status === PDP_STATE.unavailable) {
        return readAtcSoldOutLabel(button) || defaultLabel || "Sold Out";
      }
      return defaultLabel;
    }

    function syncPdpAtcLabels(atcButtons, enabled, status) {
      const buttons = Array.isArray(atcButtons) ? atcButtons : [];
      for (const button of buttons) {
        if (!button) continue;
        const expectedLabel = getExpectedPdpAtcLabel(button, enabled, status);
        if (!expectedLabel) continue;
        if (readAtcLabel(button) === expectedLabel) continue;
        setAtcLabel(button, expectedLabel);
      }
    }

    function getDesiredPdpAtcEnabled(root) {
      if (!root || !root.dataset) return null;
      if (root.dataset.ecomificPdpAtcEnabled === "1") return true;
      if (root.dataset.ecomificPdpAtcEnabled === "0") return false;
      const status = root.dataset.ecomificAvailabilityStatus || "";
      if (!status) return null;
      return status === "available";
    }

    function reassertPdpAtcState(root, reason) {
      if (!root) return;
      const desiredEnabled = getDesiredPdpAtcEnabled(root);
      if (desiredEnabled == null) return;

      const pdpEls = getPdpEls(root);
      const buttons = pdpEls.atcButtons || [];
      if (!buttons.length) return;

      const hasMismatch = buttons.some((btn) => {
        if (!btn) return false;
        const isDisabled = !!btn.disabled;
        const hasDisabledClass = !!btn.classList?.contains("disabled");
        if (desiredEnabled) {
          return isDisabled || hasDisabledClass;
        }
        return !isDisabled || !hasDisabledClass;
      });

      const pdpStatus = root.dataset?.ecomificPdpState || "";
      const hasLabelMismatch = buttons.some((btn) => {
        if (!btn) return false;
        const expected = getExpectedPdpAtcLabel(btn, desiredEnabled, pdpStatus);
        if (!expected) return false;
        return readAtcLabel(btn) !== expected;
      });

      if (!hasMismatch && !hasLabelMismatch) return;
      setAtcEnabled(buttons, desiredEnabled);
      syncPdpAtcLabels(buttons, desiredEnabled, pdpStatus);
      logInfo("pdp atc state reasserted", {
        reason,
        enabled: desiredEnabled,
        buttonCount: buttons.length,
        labelFixed: !!hasLabelMismatch,
      });
    }

    function schedulePdpAtcReassert(root, reason) {
      if (!root) return;
      if (!Array.isArray(PDP_ATC_REASSERT_DELAYS_MS) || !PDP_ATC_REASSERT_DELAYS_MS.length) {
        return;
      }

      if (Array.isArray(root.__ecomificPdpAtcReassertTimers)) {
        for (const timerId of root.__ecomificPdpAtcReassertTimers) {
          clearTimeout(timerId);
        }
      }
      root.__ecomificPdpAtcReassertTimers = [];

      for (const delay of PDP_ATC_REASSERT_DELAYS_MS) {
        const timerId = setTimeout(() => {
          reassertPdpAtcState(root, `${reason}:${delay}`);
        }, Math.max(0, Number(delay) || 0));
        root.__ecomificPdpAtcReassertTimers.push(timerId);
      }
    }

    function setQtyConstraints(qtyInput, maxQty, enabled) {
      if (!qtyInput) return;

      const numeric = Number(qtyInput.value);
      const current = Number.isFinite(numeric) && numeric > 0 ? numeric : 1;

      if (!enabled) {
        qtyInput.setAttribute("max", "1");
        qtyInput.value = "1";
        return;
      }

      if (Number.isFinite(maxQty) && maxQty > 0) {
        const floorMax = Math.max(1, Math.floor(maxQty));
        qtyInput.setAttribute("max", String(floorMax));
        qtyInput.value = String(Math.min(current, floorMax));
        return;
      }

      qtyInput.removeAttribute("max");
      qtyInput.value = String(Math.max(1, current));
    }

    function applyPdpState(els, state) {
      if (!els || !state || !state.status) return;

      const status = state.status;
      const enabled = status === PDP_STATE.available;
      const hasMessageOverride =
        typeof state.message === "string" && state.message.trim().length > 0;
      const messages = {
        [PDP_STATE.loading]: "Checking availability…",
        [PDP_STATE.available]: "",
        [PDP_STATE.unavailable]: "Unavailable in your location",
        [PDP_STATE.unknown]: "Availability can't be confirmed right now",
      };
      const message = hasMessageOverride ? state.message : (messages[status] || "");

      if (els.root?.dataset) {
        els.root.dataset.ecomificPdpAtcEnabled = enabled ? "1" : "0";
        els.root.dataset.ecomificPdpState = status;
      }
      setAtcEnabled(els.atcButtons || [], enabled);
      syncPdpAtcLabels(els.atcButtons || [], enabled, status);
      setQtyConstraints(els.qtyInput || null, state.maxQty, enabled);
      setText(els.availabilityEl || null, message);
      schedulePdpAtcReassert(els.root || null, status);

      if (els.lowStockEl) {
        const shouldShowLowStock = enabled && state.lowStock === true;
        if (shouldShowLowStock) {
          els.lowStockEl.removeAttribute("hidden");
          els.lowStockEl.style.display = "";
        } else {
          els.lowStockEl.setAttribute("hidden", "hidden");
          els.lowStockEl.style.display = "none";
        }
      }

      if (isDebugEnabled()) {
        logInfo("pdp state applied", {
          status,
          reason: state.reason || "",
          maxQty: Number.isFinite(state.maxQty) ? state.maxQty : null,
          lowStock: state.lowStock === true,
          atcButtons: (els.atcButtons || []).length,
          qtyInput: !!els.qtyInput,
        });
      }
    }

    function toPdpState(state) {
      const status = state?.status;
      if (status === "loading") {
        return {
          status: PDP_STATE.loading,
          reason: "controller-loading",
          message: state?.message || null,
        };
      }
      if (status === "available") {
        return {
          status: PDP_STATE.available,
          reason: "controller-available",
          maxQty: state?.max_quantity,
          lowStock: state?.lowStock === true,
          message: state?.message || null,
        };
      }
      if (status === "unknown") {
        return {
          status: PDP_STATE.unknown,
          reason: "controller-unknown",
          message: state?.message || null,
        };
      }
      return {
        status: PDP_STATE.unavailable,
        reason: "controller-unavailable",
        message: state?.message || null,
      };
    }

    function applyPdpLoadingState(root = document, reason = "loading") {
      const scope = root && root.nodeType === 1 ? root : document;
      const pdpRoots = scope.matches?.(PDP_DOM.root)
        ? [scope]
        : Array.from(scope.querySelectorAll?.(PDP_DOM.root) || []);

      for (const pdpRoot of pdpRoots) {
        const els = getPdpEls(pdpRoot);
        logPdpDomContract(pdpRoot, els);
        applyPdpState(els, {
          status: PDP_STATE.loading,
          reason,
        });
      }
    }
  
    function getSlots(container) {
      const atcElements = Array.from(container.querySelectorAll("[data-ecomific-atc]"));
      const atcForm = atcElements.map((el) => el.closest("form")).find(Boolean)
        || container.querySelector('form[action*="/cart/add"]');
      let atc = atcForm ? atcElements.filter((el) => atcForm.contains(el)) : atcElements;
      const variantInput = atcForm
        ? (atcForm.querySelector("[data-ecomific-variant-input]") || atcForm.querySelector('input[name="id"], select[name="id"]'))
        : (container.querySelector("[data-ecomific-variant-input]") || container.querySelector('input[name="id"], select[name="id"]'));

      let message = container.querySelector("[data-ecomific-availability-message]");
      let qty = Array.from(container.querySelectorAll("[data-ecomific-qty-input]"));
      let lowStock = null;
      let status = null;

      if (container.matches?.(PDP_DOM.root)) {
        const pdpEls = getPdpEls(container);
        logPdpDomContract(container, pdpEls);
        if (pdpEls.atcButtons.length) {
          atc = pdpEls.atcButtons;
        }
        if (pdpEls.qtyInput) {
          qty = [pdpEls.qtyInput];
        }
        if (pdpEls.availabilityEl) {
          message = pdpEls.availabilityEl;
        }
        lowStock = pdpEls.lowStockEl || null;
        status = pdpEls.statusEl || null;
      }

      return {
        message,
        badges: container.querySelector("[data-ecomific-badges]"),
        atc,
        qty,
        dynamicCheckout: Array.from(container.querySelectorAll("[data-ecomific-dynamic-checkout]")),
        variantInput,
        lowStock,
        status,
      };
    }
  
    function setText(el, text) {
      if (!el) return;
      el.textContent = text || "";
    }

    function readSessionCache(key, ttlMs) {
      try {
        const raw = window.sessionStorage?.getItem(`${SESSION_CACHE_PREFIX}${key}`);
        if (!raw) return null;
        const payload = JSON.parse(raw);
        if (!payload || !Number.isFinite(payload.ts)) return null;
        if (Date.now() - payload.ts > ttlMs) return null;
        return payload;
      } catch (err) {
        return null;
      }
    }

    function writeSessionCache(key, payload) {
      try {
        const data = { ...payload, ts: Date.now() };
        window.sessionStorage?.setItem(
          `${SESSION_CACHE_PREFIX}${key}`,
          JSON.stringify(data)
        );
      } catch (err) {
        // ignore storage errors
      }
    }
  
    function normalizeElements(elOrList) {
      if (!elOrList) return [];
      if (Array.isArray(elOrList)) return elOrList;
      if (typeof NodeList !== "undefined" && elOrList instanceof NodeList) {
        return Array.from(elOrList);
      }
      return [elOrList];
    }
  
    function bindNonButtonGuard(el) {
      if (!el || el.__ecomificAvailabilityGuard) return;
      el.__ecomificAvailabilityGuard = true;
      el.addEventListener("click", (event) => {
        if (el.getAttribute("aria-disabled") === "true") {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);
    }

    function setDisabled(elOrList, disabled) {
      const elements = normalizeElements(elOrList);
      if (elements.length === 0) return;
  
      for (const el of elements) {
        if (!el) continue;
  
        // If slot wraps a button, prefer the actual button
        const btn =
          el.matches("button, input[type='submit'], input[type='button']") ? el :
          el.querySelector?.("button, input[type='submit'], input[type='button']");
  
        if (btn) {
          btn.disabled = !!disabled;
          if (btn.classList) {
            btn.classList.toggle("disabled", !!disabled);
          }
          if (disabled) {
            btn.setAttribute("aria-disabled", "true");
          } else {
            btn.removeAttribute("aria-disabled");
          }
          continue;
        }
  
        // Fallback: aria-disabled on container / link
        if (disabled) {
          el.setAttribute("aria-disabled", "true");
          el.classList.add("is-ecomific-disabled");
          bindNonButtonGuard(el);
        } else {
          el.removeAttribute("aria-disabled");
          el.classList.remove("is-ecomific-disabled");
        }
        if (el.matches("a")) {
          el.tabIndex = disabled ? -1 : 0;
        }
      }
    }

    function setVisibility(elOrList, visible) {
      const elements = normalizeElements(elOrList);
      if (elements.length === 0) return;
      for (const el of elements) {
        if (!el) continue;
        el.style.display = visible ? "" : "none";
      }
    }
  
    function getQtyInputs(qtySlots) {
      const elements = normalizeElements(qtySlots);
      const inputs = [];
      for (const el of elements) {
        if (!el) continue;
        if (el.matches("input")) {
          inputs.push(el);
        } else {
          const input = el.querySelector?.("input");
          if (input) inputs.push(input);
        }
      }
      return inputs;
    }
  
    function clearStateClasses(container) {
      container.classList.remove("is-available", "is-coming-soon", "is-sold-out", "is-unknown", "is-loading");
    }
  
    function variantSoldOutAtShopifyPage(container, slots) {
      // Shopify's own word on the variant, read from the theme's product
      // JSON (textarea[data-variant-json] on the PDP). Only a positive
      // "available": false blocks; any doubt keeps endpoint-driven behavior.
      try {
        const rawVariantId =
          slots.variantInput?.value ||
          container.dataset.activeVariantId ||
          container.dataset.variantId;
        const variantId = normalizeVariantId(rawVariantId);
        if (!variantId) return false;
        const nodes = document.querySelectorAll("[data-variant-json]");
        for (const node of nodes) {
          let variants;
          try {
            variants = JSON.parse(node.value || node.textContent || "");
          } catch (parseError) {
            continue;
          }
          if (!Array.isArray(variants)) continue;
          for (const candidate of variants) {
            if (candidate && String(candidate.id) === String(variantId)) {
              return candidate.available === false;
            }
          }
        }
      } catch (error) {}
      return false;
    }

    function applyState(container, slots, state, cfg) {
      if (state && state.status !== "sold_out" && variantSoldOutAtShopifyPage(container, slots)) {
        // Sold out at Shopify itself; never present the variant as buyable,
        // whatever the regional endpoint said (or failed to say).
        state = Object.assign({}, state, {
          status: "sold_out",
          can_add_to_cart: false,
          max_quantity: null,
          message: cfg.soldOutCopy || "Out of stock",
        });
      }
      clearStateClasses(container);
  
      container.dataset.ecomificAvailabilityStatus = state.status;
  
      if (state.status === "loading") container.classList.add("is-loading");
      if (state.status === "available") container.classList.add("is-available");
      if (state.status === "coming_soon") container.classList.add("is-coming-soon");
      if (state.status === "sold_out") container.classList.add("is-sold-out");
      if (state.status === "unknown") container.classList.add("is-unknown");
  
      // ATC gating (spec: can_add_to_cart true only for available) :contentReference[oaicite:2]{index=2}
      const atcDisabled =
        state.status === "available" ? false :
        state.status === "loading" ? true :
        state.status === "unknown" ? !!cfg.blockAtcWhenUnknown : true;
  
      setDisabled(slots.atc, atcDisabled);
      setVisibility(slots.dynamicCheckout, state.status === "available");
  
      // Qty constraints
      const qtyInputs = getQtyInputs(slots.qty);
      if (qtyInputs.length) {
        for (const qtyInput of qtyInputs) {
          if (!qtyInput) continue;
          if (state.max_quantity != null && Number.isFinite(state.max_quantity)) {
            qtyInput.setAttribute("max", String(state.max_quantity));
            const current = Number(qtyInput.value);
            if (Number.isFinite(current) && current > state.max_quantity) {
              qtyInput.value = String(state.max_quantity);
            }
          } else {
            qtyInput.removeAttribute("max");
          }
        }
      }
  
      // Keep textual availability copy PDP-only. PLP uses badge chips.
      const isPdpContainer = !!container.matches?.(PDP_DOM.root);
      setText(slots.message, isPdpContainer ? (state.message || "") : "");

      if (isPdpContainer) {
        applyPdpState(getPdpEls(container), toPdpState(state));
      } else {
        syncCardAtcLabels(slots.atc, state, cfg);
      }
  
      // Badges (optional slot)
      if (slots.badges) {
        slots.badges.classList.remove("grid-product__tag--sold-out");
        if (state.status === "coming_soon") {
          setText(slots.badges, cfg.comingSoonCopy);
        } else if (state.status === "sold_out") {
          setText(slots.badges, cfg.soldOutCopy);
          slots.badges.classList.add("grid-product__tag--sold-out");
        } else {
          setText(slots.badges, "");
        }
      }

      // Add/remove card-level class to prevent duplicate sold-out badges
      var cardRoot = null;
      try {
        cardRoot = container && container.closest ? container.closest('[data-ecomific-product-card]') : null;
      } catch (e) {
        cardRoot = null;
      }

      if (cardRoot && cardRoot.classList) {
        if (state.status === 'sold_out') {
          cardRoot.classList.add('ecomific-regional-sold-out');
        } else {
          cardRoot.classList.remove('ecomific-regional-sold-out');
        }
      }

      if (container.matches?.("[data-ecomific-pdp]")) {
        const bisTrigger =
          container.querySelector(".klaviyo-bis-trigger") ||
          document.querySelector(".klaviyo-bis-trigger");
        if (bisTrigger) {
          const showNotify =
            state.status === "coming_soon" || state.status === "sold_out";
          setVisibility(bisTrigger, showNotify);
        }
      }
    }
  
    function computeComingSoon({ launchPending, productCreatedAtMs, nowMs, regionalQty, cfg }) {
      if (!launchPending) return false;
      if (regionalQty > 0) return false;
  
      if (!productCreatedAtMs) return false;
      const ageMs = nowMs - productCreatedAtMs;
      return ageMs >= 0 && ageMs <= cfg.launchPendingMaxDays * DAY_MS;
    }
  
    function computeState({
      ok,
      qty,
      launchPending,
      createdAtMs,
      cfg,
      tracked,
      availableForSale,
      inventoryPolicy,
    }) {
      const nowMs = Date.now();
      const usesShopifyNativeAvailability =
        availableForSale === true &&
        (tracked === false || inventoryPolicy === "CONTINUE");
  
      if (!ok) {
        return { status: "unknown", can_add_to_cart: false, max_quantity: null, message: cfg.unknownCopy };
      }

      if (usesShopifyNativeAvailability) {
        return {
          status: "available",
          can_add_to_cart: true,
          max_quantity: null,
          lowStock: false,
          message: "",
        };
      }

      if (tracked === false) {
        return {
          status: "sold_out",
          can_add_to_cart: false,
          max_quantity: 0,
          message: cfg.soldOutCopy,
        };
      }
  
      if (qty > 0) {
        let message = "";
        const isLow = qty <= cfg.lowStockThreshold;
  
        if (isLow) {
          if (cfg.showExactQtyWhenLowStock) {
            message = `${cfg.onlyXLeftPrefix}${qty}${cfg.onlyXLeftSuffix}`;
          } else {
            message = cfg.lowStockCopy;
          }
        }
  
        return { status: "available", can_add_to_cart: true, max_quantity: qty, lowStock: isLow, message };
      }
  
      // qty === 0
      const isComingSoon = computeComingSoon({
        launchPending,
        productCreatedAtMs: createdAtMs,
        nowMs,
        regionalQty: qty,
        cfg,
      });
  
      if (isComingSoon) {
        return { status: "coming_soon", can_add_to_cart: false, max_quantity: 0, message: cfg.comingSoonCopy };
      }
  
      return { status: "sold_out", can_add_to_cart: false, max_quantity: 0, message: cfg.soldOutCopy };
    }
  
    class AvailabilityController {
      constructor(cfg) {
        this.cfg = cfg;
        this.cache = new Map(); // key: `${variantId}:${region}` => { ok, quantity, fetchedAtMs }
        this.inflight = new Map(); // key: `${variantId}:${region}` => Promise
        this.containerRequestSeq = new WeakMap(); // container => latest hydrate sequence
        this.fetchQueue = [];
        this.activeFetches = 0;
        this.region = null;
        this.hydrateDebounceId = null;
        this.domObserver = null;
        this._initPromise = null;
        this._missingEndpointWarned = false;
        this._destroyed = false;
        this._initRunId = 0;
        this._lifecycleBound = false;
        this._listenersBound = false;
        this._endpointDebugged = false;
        this._variantChangeFallbackBound = false;
  
        this.onRegionChanged = this.onRegionChanged.bind(this);
        this.onRegionReady = this.onRegionReady.bind(this);
        this.onVariantChanged = this.onVariantChanged.bind(this);
        this.onVariantIdInputChanged = this.onVariantIdInputChanged.bind(this);
        this.scheduleHydrateAll = this.scheduleHydrateAll.bind(this);
        this.onLifecycleExit = this.onLifecycleExit.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.drainFetchQueue = this.drainFetchQueue.bind(this);
      }

      drainFetchQueue() {
        while (
          this.activeFetches < this.cfg.maxConcurrentRequests &&
          this.fetchQueue.length > 0
        ) {
          const task = this.fetchQueue.shift();
          task();
        }
      }

      enqueueFetch(key, task) {
        if (this.inflight.has(key)) return this.inflight.get(key);
        const promise = new Promise((resolve, reject) => {
          const run = () => {
            this.activeFetches += 1;
            task()
              .then(resolve)
              .catch(reject)
              .finally(() => {
                this.activeFetches -= 1;
                this.inflight.delete(key);
                this.drainFetchQueue();
              });
          };
          this.fetchQueue.push(run);
          this.drainFetchQueue();
        });
        this.inflight.set(key, promise);
        return promise;
      }
  
      scheduleHydrateAll() {
        if (this._destroyed) return;
        if (this.hydrateDebounceId) {
          clearTimeout(this.hydrateDebounceId);
        }
        this.hydrateDebounceId = setTimeout(() => {
          this.hydrateDebounceId = null;
          this.hydrateAll();
        }, 75);
      }

      async init() {
        if (this._initPromise) return this._initPromise;

        this._initPromise = (async () => {
          this._destroyed = false;
          const runId = ++this._initRunId;

          if (!this._listenersBound) {
            window.addEventListener("ecomific:region:ready", this.onRegionReady);
            window.addEventListener("ecomific:region:changed", this.onRegionChanged);
            document.addEventListener("variant:change", this.onVariantChanged);
            this._listenersBound = true;
          }
          if (!this._variantChangeFallbackBound) {
            document.addEventListener("change", this.onVariantIdInputChanged, true);
            this._variantChangeFallbackBound = true;
          }
          if (!this._lifecycleBound) {
            window.addEventListener("pagehide", this.onLifecycleExit);
            window.addEventListener("beforeunload", this.onLifecycleExit);
            window.addEventListener("pageshow", this.onPageShow);
            this._lifecycleBound = true;
          }
  
          this.region = await getActiveRegionFromDomOrCart();
          if (this._destroyed || runId !== this._initRunId) return;
          applyPdpLoadingState(document, "init");
          if (!this.region) {
            logInfo("waiting for region before hydrating");
            return;
          }
  
          this.scheduleHydrateAll();
  
          // Observe DOM changes (PLP infinite scroll, predictive search, etc.)
          this.observeDom();
        })();

        try {
          return await this._initPromise;
        } finally {
          this._initPromise = null;
        }
      }
  
      onRegionChanged(e) {
        const next = e?.detail?.region ? normalizeRegion(e.detail.region) : null;
        if (next) cachedRegionFromEvents = next;
        if (!next || next === this.region) return;
        this.region = next;
        this.cache.clear();
        this.observeDom();
        this.scheduleHydrateAll();
      }

      onRegionReady(e) {
        const next = e?.detail?.region ? normalizeRegion(e.detail.region) : null;
        if (next) cachedRegionFromEvents = next;
        if (!next || next === this.region) return;
        this.region = next;
        this.cache.clear();
        this.observeDom();
        this.scheduleHydrateAll();
      }
  
      onVariantChanged(event) {
        const root = event?.target?.closest?.(PDP_DOM.root) || document;
        applyPdpLoadingState(root, "variant-change");
        this.scheduleHydrateAll();
      }

      onVariantIdInputChanged(event) {
        const target = event?.target;
        if (!target || !target.matches?.("input, select")) return;
        if (target.name !== "id") return;
        if (!target.closest?.(CONTAINER_SELECTOR)) return;
        const root = target.closest(PDP_DOM.root) || document;
        applyPdpLoadingState(root, "variant-id-change");
        this.scheduleHydrateAll();
      }

      onLifecycleExit() {
        this.destroy();
      }

      onPageShow(event) {
        if (!event || !event.persisted) return;
        this.destroy();
        this.init();
      }

      destroy() {
        this._destroyed = true;
        this._initRunId += 1;
        this._initPromise = null;

        if (this.hydrateDebounceId) {
          clearTimeout(this.hydrateDebounceId);
          this.hydrateDebounceId = null;
        }

        if (this.domObserver) {
          this.domObserver.disconnect();
          this.domObserver = null;
        }

        const pdpRoots = Array.from(document.querySelectorAll(PDP_DOM.root));
        for (const root of pdpRoots) {
          if (!Array.isArray(root.__ecomificPdpAtcReassertTimers)) continue;
          for (const timerId of root.__ecomificPdpAtcReassertTimers) {
            clearTimeout(timerId);
          }
          root.__ecomificPdpAtcReassertTimers = [];
        }

        window.removeEventListener("ecomific:region:ready", this.onRegionReady);
        window.removeEventListener("ecomific:region:changed", this.onRegionChanged);
        document.removeEventListener("variant:change", this.onVariantChanged);
        this._listenersBound = false;
        if (this._variantChangeFallbackBound) {
          document.removeEventListener("change", this.onVariantIdInputChanged, true);
          this._variantChangeFallbackBound = false;
        }

        if (this._lifecycleBound) {
          window.removeEventListener("pagehide", this.onLifecycleExit);
          window.removeEventListener("beforeunload", this.onLifecycleExit);
          window.removeEventListener("pageshow", this.onPageShow);
          this._lifecycleBound = false;
        }

      }
  
      applyAllUnknown(reason) {
        const nodes = this.getAllContainers();
        for (const container of nodes) {
          const slots = getSlots(container);
          applyState(container, slots, { status: "unknown", message: this.cfg.unknownCopy, max_quantity: null }, this.cfg);
        }
        logWarn("unknown", { reason });
      }
  
      getAllContainers() {
        return [
          ...document.querySelectorAll(CONTAINER_SELECTOR),
        ];
      }
  
      observeDom() {
        if (this._destroyed) return;
        if (this.domObserver) {
          this.domObserver.disconnect();
        }

        const observer = new MutationObserver((mutations) => {
          // If new product cards or PDP fragments appear, hydrate them.
          let shouldHydrate = false;
          for (const m of mutations) {
            if (m.target?.closest?.("[data-ecomific-availability-message],[data-ecomific-badges],[data-ecomific-dynamic-checkout]")) {
              continue;
            }
            if (m.type === "childList" && (m.addedNodes?.length || 0) > 0) {
              for (const node of m.addedNodes) {
                if (!node || node.nodeType !== 1) continue;
                if (node.matches?.(CONTAINER_SELECTOR) || node.querySelector?.(CONTAINER_SELECTOR)) {
                  shouldHydrate = true;
                  break;
                }
              }
              if (shouldHydrate) break;
            }
          }
          if (shouldHydrate) this.scheduleHydrateAll();
        });
  
        observer.observe(document.documentElement, { childList: true, subtree: true });
        this.domObserver = observer;
      }
  
      hydrateAll() {
        if (this._destroyed) return;
        const containers = this.getAllContainers();
        for (const container of containers) {
          this.hydrateOne(container);
        }
      }
  
      hydrateOne(container) {
        const slots = getSlots(container);
        const requestSeq = (this.containerRequestSeq.get(container) || 0) + 1;
        this.containerRequestSeq.set(container, requestSeq);
  
        // Determine variant id:
        // - prefer hidden input[name=id] (theme product form)
        // - else data-active-variant-id (PDP) or data-variant-id (PLP)
        const rawVariantId =
          slots.variantInput?.value ||
          container.dataset.activeVariantId ||
          container.dataset.variantId;
  
        const variantId = normalizeVariantId(rawVariantId);
        if (!variantId) return;
  
        // Coming soon inputs (Flow metafield + product.created_at) :contentReference[oaicite:5]{index=5}
        const launchPending = parseBool(container.dataset.launchPending);
        const createdAtMs = parseDateMs(container.dataset.productCreatedAt);
  
        // Loading state
        applyState(container, slots, { status: "loading", message: this.cfg.loadingCopy, max_quantity: null }, this.cfg);
  
        // Wire variant change (PDP)
        if (slots.variantInput && !slots.variantInput.__ecomificAvailabilityBound) {
          slots.variantInput.__ecomificAvailabilityBound = true;
          slots.variantInput.addEventListener("change", () => this.hydrateOne(container));
        }
  
        this.fetchAndApply({ container, slots, variantId, launchPending, createdAtMs, requestSeq });
      }

      getCurrentVariantId(container) {
        if (!container) return null;
        const slots = getSlots(container);
        const rawVariantId =
          slots.variantInput?.value ||
          container.dataset.activeVariantId ||
          container.dataset.variantId;
        return normalizeVariantId(rawVariantId);
      }

      isStaleRequest(container, expectedVariantId, expectedRegion, requestSeq) {
        if (!container || this._destroyed) return true;

        const latestSeq = this.containerRequestSeq.get(container) || 0;
        if (latestSeq !== requestSeq) return true;

        if (expectedRegion && this.region && expectedRegion !== this.region) {
          return true;
        }

        const currentVariantId = this.getCurrentVariantId(container);
        if (expectedVariantId && currentVariantId && expectedVariantId !== currentVariantId) {
          return true;
        }

        return false;
      }
  
      async fetchAndApply({ container, slots, variantId, launchPending, createdAtMs, requestSeq }) {
        const region = this.region;
        if (!region) {
          logWarn("fetch skipped (no region)");
          return;
        }
        const key = `${variantId}:${region}`;
  
        if (this.cache.has(key)) {
          const cached = this.cache.get(key);
          const state = computeState({
            ok: cached.ok,
            qty: cached.quantity,
            launchPending,
            createdAtMs,
            cfg: this.cfg,
            tracked: cached.tracked,
            availableForSale: cached.availableForSale,
            inventoryPolicy: cached.inventoryPolicy,
          });
          if (this.isStaleRequest(container, variantId, region, requestSeq)) {
            logDebug("stale availability apply skipped", {
              source: "memory-cache",
              expectedVariantId: variantId,
              expectedRegion: region,
              currentRegion: this.region || null,
            });
            return;
          }
          applyState(container, slots, state, this.cfg);
          return;
        }
  
        const sessionCached = readSessionCache(key, this.cfg.sessionCacheTtlMs);
        if (sessionCached) {
          this.cache.set(key, {
            ok: sessionCached.ok,
            quantity: sessionCached.quantity,
            tracked: sessionCached.tracked,
            availableForSale: sessionCached.availableForSale,
            inventoryPolicy: sessionCached.inventoryPolicy,
            fetchedAtMs: sessionCached.ts,
          });
          const state = computeState({
            ok: sessionCached.ok,
            qty: sessionCached.quantity,
            launchPending,
            createdAtMs,
            cfg: this.cfg,
            tracked: sessionCached.tracked,
            availableForSale: sessionCached.availableForSale,
            inventoryPolicy: sessionCached.inventoryPolicy,
          });
          if (this.isStaleRequest(container, variantId, region, requestSeq)) {
            logDebug("stale availability apply skipped", {
              source: "session-cache",
              expectedVariantId: variantId,
              expectedRegion: region,
              currentRegion: this.region || null,
            });
            return;
          }
          applyState(container, slots, state, this.cfg);
          return;
        }

        if (this.inflight.has(key)) {
          try {
            const inflight = await this.inflight.get(key);
            const state = computeState({
              ok: inflight.ok,
              qty: inflight.quantity,
              launchPending,
              createdAtMs,
              cfg: this.cfg,
              tracked: inflight.tracked,
              availableForSale: inflight.availableForSale,
              inventoryPolicy: inflight.inventoryPolicy,
            });
            if (this.isStaleRequest(container, variantId, region, requestSeq)) {
              logDebug("stale availability apply skipped", {
                source: "inflight",
                expectedVariantId: variantId,
                expectedRegion: region,
                currentRegion: this.region || null,
              });
              return;
            }
            applyState(container, slots, state, this.cfg);
            return;
          } catch (err) {
            // fall through to fetch attempt below
          }
        }

        const runFetch = async () => {
          const base = normalizeEndpointBase(this.cfg.endpointBase || "");
          const endpoint = buildInventoryEndpoint(base);
          if (!endpoint) {
            if (!this._missingEndpointWarned) {
              this._missingEndpointWarned = true;
              logWarn("endpointBase must be an absolute http(s) Netlify URL");
            }
              const fallback = {
                ok: false,
                quantity: 0,
                tracked: null,
                availableForSale: false,
                inventoryPolicy: null,
                fetchedAtMs: Date.now()
              };
              this.cache.set(key, fallback);
              writeSessionCache(key, {
                ok: fallback.ok,
                quantity: fallback.quantity,
                tracked: fallback.tracked,
                availableForSale: fallback.availableForSale,
                inventoryPolicy: fallback.inventoryPolicy,
              });
              return fallback;
          }

          if (!this._endpointDebugged) {
            this._endpointDebugged = true;
            logInfo("availability endpoint", {
              endpointBase: this.cfg.endpointBase,
              normalizedBase: base,
              finalEndpoint: endpoint
            });
          }

          const url = `${endpoint}?variant_id=${encodeURIComponent(variantId)}&region=${encodeURIComponent(region)}`;

          try {
            const res = await withTimeout(fetch(url, { credentials: "omit" }), this.cfg.requestTimeoutMs);
            const json = await res.json();
  
            const ok = !!json?.ok;
            const tracked =
              typeof json?.tracked === "boolean" ? json.tracked : null;
            const availableForSale = json?.available_for_sale === true;
            const inventoryPolicy =
              typeof json?.inventory_policy === "string"
                ? String(json.inventory_policy).trim().toUpperCase()
                : null;
            const quantity =
              ok && json?.quantity != null ? Number(json.quantity || 0) : 0;

            const next = {
              ok,
              quantity,
              tracked,
              availableForSale,
              inventoryPolicy,
              fetchedAtMs: Date.now()
            };
            this.cache.set(key, next);
            writeSessionCache(key, {
              ok,
              quantity,
              tracked,
              availableForSale,
              inventoryPolicy,
            });
            return next;
          } catch (err) {
            const fallback = {
              ok: false,
              quantity: 0,
              tracked: null,
              availableForSale: false,
              inventoryPolicy: null,
              fetchedAtMs: Date.now()
            };
            this.cache.set(key, fallback);
            writeSessionCache(key, {
              ok: fallback.ok,
              quantity: fallback.quantity,
              tracked: fallback.tracked,
              availableForSale: fallback.availableForSale,
              inventoryPolicy: fallback.inventoryPolicy,
            });
            return fallback;
          }
        };

        const cached = await this.enqueueFetch(key, runFetch);
        const state = computeState({
          ok: cached.ok,
          qty: cached.quantity,
          launchPending,
          createdAtMs,
          cfg: this.cfg,
          tracked: cached.tracked,
          availableForSale: cached.availableForSale,
          inventoryPolicy: cached.inventoryPolicy,
        });
        if (this.isStaleRequest(container, variantId, region, requestSeq)) {
          logDebug("stale availability apply skipped", {
            source: "network",
            expectedVariantId: variantId,
            expectedRegion: region,
            currentRegion: this.region || null,
          });
          return;
        }
        applyState(container, slots, state, this.cfg);
      }
    }
  
    function boot() {
      const cfg = getConfig();
      const controller = new AvailabilityController(cfg);
      window.__ecomificAvailabilityController = controller;
      controller.init();
      window.ecomificAvailabilityController = controller;
    }
  
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }
  })();
  
